import { ReactNode, useContext, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Grid,
  IconButton,
  SvgIcon,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { Search } from '@mui/icons-material';
import CollectionsBookmarkOutlined from '@mui/icons-material/CollectionsBookmarkOutlined';
import Inventory2Outlined from '@mui/icons-material/Inventory2Outlined';
import EventAvailableOutlined from '@mui/icons-material/EventAvailableOutlined';
import FiberNewOutlined from '@mui/icons-material/FiberNewOutlined';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Trash01 } from '../../../../assets';
import { rangeClient, RangeDto, RangeStatsDto } from '../../../../app/services/api/range';
import useLogError from '../../../../hooks/useLogError';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { EmptyTable } from '../../../../components/shared/EmptyTable';
import { UserContext } from '../../../../components/shared/useUser';
import { TableColumn, TableSectionProps, TableSortDirection } from '../../../../components/shared/tables/TableSection';
import { InsightTile } from '../../../../components/shared/tiles/InsightTile';
import type { ListPageHeadline } from '../../../../components/page-templates/list-page/ListPageTemplate';

const PAGE_SIZE = 20;

type Props = {
  onFilterChange?: (_searchTerm?: string) => void;
  initialSearchTerm?: string;
  children: (_parts: {
    filterBar: ReactNode;
    headlineContent: ListPageHeadline;
    table: TableSectionProps<RangeDto>;
    isInitialLoading: boolean;
  }) => ReactNode;
};

const formatDate = (value?: string | null): string => (
  value ? new Date(value).toLocaleDateString() : '-'
);

const RangesListPageContent = ({
  onFilterChange,
  initialSearchTerm,
  children,
}: Props) => {
  const { t } = useTranslation('range');
  const theme = useTheme();
  const navigate = useNavigate();
  const {
    franchises,
    hasPermissionTo,
  } = useContext(UserContext);
  const { logError } = useLogError();

  const canView = hasPermissionTo(['RangeRead']);
  const canWrite = hasPermissionTo(['RangeWrite']);
  const canDelete = hasPermissionTo(['RangeWrite']);
  const [searchInput, setSearchInput] = useState(initialSearchTerm ?? '');
  const [searchText, setSearchText] = useState(initialSearchTerm ?? '');
  const [currentPage, setCurrentPage] = useState(0);
  const [sortBy, setSortBy] = useState('name');
  const [sortDirection, setSortDirection] = useState<TableSortDirection>('asc');
  const [ranges, setRanges] = useState<RangeDto[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState<RangeStatsDto>({
    totalRanges: 0,
    emptyRanges: 0,
    scheduledOnlyRanges: 0,
    newRanges: 0,
  });
  const [isLoading, setIsLoading] = useState(false);
  const [isStatsLoading, setIsStatsLoading] = useState(false);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<RangeDto | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  useEffect(() => {
    setSearchInput(initialSearchTerm ?? '');
    setSearchText(initialSearchTerm ?? '');
  }, [initialSearchTerm]);

  const franchiseNameById = useMemo(() => new Map(
    franchises
      .filter((franchise) => franchise.franchiseId !== undefined)
      .map((franchise) => [franchise.franchiseId as number, franchise.franchiseName]),
  ), [franchises]);

  useEffect(() => {
    let active = true;
    setIsLoading(true);
    rangeClient.getRanges({
      search: searchText.trim() || undefined,
      page: currentPage + 1,
      pageSize: PAGE_SIZE,
      sortBy,
      sortDirection,
    })
      .then((result) => {
        if (!active) return;
        setRanges(result.data);
        setTotalCount(result.totalCount);
      })
      .catch((error) => {
        if (!active) return;
        logError(error);
        setRanges([]);
        setTotalCount(0);
        setFeedback({ message: t('loadErrorMessage'), severity: 'error' });
      })
      .finally(() => {
        if (!active) return;
        setIsLoading(false);
        setHasLoadedOnce(true);
      });

    return () => {
      active = false;
    };
    // logError is intentionally omitted: useLogError returns a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchText, currentPage, sortBy, sortDirection]);

  useEffect(() => {
    let active = true;
    setIsStatsLoading(true);
    rangeClient.getRangeStats()
      .then((result) => {
        if (active) setStats(result);
      })
      .catch((error) => {
        if (active) logError(error);
      })
      .finally(() => {
        if (active) setIsStatsLoading(false);
      });

    return () => {
      active = false;
    };
    // logError is intentionally omitted: useLogError returns a new function on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const updateFilters = (nextSearch = searchText) => {
    onFilterChange?.(nextSearch);
  };

  const handleSearch = () => {
    setIsLoading(true);
    setSearchText(searchInput.trim());
    setCurrentPage(0);
    updateFilters(searchInput.trim());
  };

  const handleClear = () => {
    setIsLoading(true);
    setSearchInput('');
    setSearchText('');
    setCurrentPage(0);
    updateFilters('');
  };

  const handleSortChange = (nextSortBy: string, nextSortDirection: TableSortDirection) => {
    setIsLoading(true);
    setSortBy(nextSortBy);
    setSortDirection(nextSortDirection);
    setCurrentPage(0);
  };

  const handlePageChange = (nextPage: number) => {
    setIsLoading(true);
    setCurrentPage(nextPage);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) return;
    setIsDeleting(true);
    try {
      await rangeClient.deleteRange(deleteCandidate.id);
      setFeedback({ message: t('deleteSuccessMessage'), severity: 'success' });
      setDeleteCandidate(null);
      setRanges((current) => current.filter((range) => range.id !== deleteCandidate.id));
      setTotalCount((current) => Math.max(current - 1, 0));
      const refreshedStats = await rangeClient.getRangeStats();
      setStats(refreshedStats);
    } catch (error) {
      logError(error);
      setFeedback({ message: t('deleteErrorMessage'), severity: 'error' });
    } finally {
      setIsDeleting(false);
    }
  };

  const columns: TableColumn<RangeDto>[] = [
    {
      id: 'name',
      label: t('name'),
      sortable: true,
      sortKey: 'name',
      render: (row) => <Typography variant="textSM"
        fontWeight={500}>{row.name}</Typography>,
    },
    {
      id: 'franchise',
      label: t('franchise'),
      render: (row) => franchiseNameById.get(row.franchiseId) ?? String(row.franchiseId),
    },
    { id: 'description', label: t('description'), sortable: true, sortKey: 'description', render: (row) => row.description || t('notSet') },
    { id: 'menus', label: t('menus'), sortable: true, sortKey: 'menuCount', render: (row) => row.menuCount },
    { id: 'createdDate', label: t('createdOn'), sortable: true, sortKey: 'createdDate', render: (row) => formatDate(row.createdDate) },
    { id: 'modifiedDate', label: t('modifiedOn'), sortable: true, sortKey: 'modifiedDate', render: (row) => formatDate(row.modifiedDate ?? row.createdDate) },
  ];

  if (canDelete) {
    columns.push({
      id: 'actions',
      label: t('actions'),
      render: (row) => (
        <IconButton
          aria-label={t('delete')}
          size="small"
          onClick={(event) => {
            event.stopPropagation();
            setDeleteCandidate(row);
          }}
        >
          <SvgIcon sx={{ color: theme.palette.error[500] }}><Trash01 /></SvgIcon>
        </IconButton>
      ),
    });
  }

  const table: TableSectionProps<RangeDto> = {
    title: t('ranges'),
    columns,
    rows: ranges,
    getRowId: (row) => row.id,
    onRowClick: canView ? (row) => navigate(`/range/ranges/${row.id}`) : undefined,
    recordCount: totalCount,
    recordCountLabel: t('ranges').toLowerCase(),
    loading: !hasLoadedOnce,
    loadingOverlay: isLoading && hasLoadedOnce,
    emptyContent: (
      <EmptyTable
        bodyRowHeight={56}
        headerHeight={45}
        text={t('ranges')}
        searchTerm={searchInput}
        onClear={handleClear}
        onAdd={() => navigate('/range/ranges/new')}
        onAddIsCreating={false}
        showOnAdd={canWrite}
        addItemText={t('createRange')}
        withPaginationShown={false}
      />
    ),
    page: currentPage,
    maxPages: Math.max(Math.ceil(totalCount / PAGE_SIZE), 1),
    onPageChange: handlePageChange,
    sortBy,
    sortDirection,
    onSortChange: handleSortChange,
  };

  const filterBar = (
    <Grid container
      spacing={5}>
      <Grid item
        lg={3}
        sm={6}
        xs={12}>
        <TextField
          fullWidth
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          onChange={(event) => setSearchInput(event.target.value)}
          onKeyDown={(event) => event.key === 'Enter' && handleSearch()}
        />
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button fullWidth
          variant="secondary"
          size="lg"
          onClick={handleClear}>{t('clear')}</Button>
      </Grid>
      <Grid item
        lg={2}
        sm={3}
        xs={6}>
        <Button
          fullWidth
          variant="primary"
          size="lg"
          onClick={handleSearch}
          startIcon={<SvgIcon><Search /></SvgIcon>}
        >
          {t('search')}
        </Button>
      </Grid>
    </Grid>
  );

  const headlineContent: ListPageHeadline = {
    insights: [
      <InsightTile key="total"
        label={t('totalRanges')}
        value={stats.totalRanges}
        description={t('totalRangesDescription')}
        icon={<CollectionsBookmarkOutlined />}
        variant="primary"
        loading={isStatsLoading && !hasLoadedOnce} />,
      <InsightTile key="empty"
        label={t('emptyRanges')}
        value={stats.emptyRanges}
        description={t('emptyRangesDescription')}
        icon={<Inventory2Outlined />}
        variant="warning"
        loading={isStatsLoading && !hasLoadedOnce} />,
      <InsightTile key="scheduled"
        label={t('scheduledOnly')}
        value={stats.scheduledOnlyRanges}
        description={t('scheduledOnlyDescription')}
        icon={<EventAvailableOutlined />}
        variant="secondary"
        loading={isStatsLoading && !hasLoadedOnce} />,
      <InsightTile key="new"
        label={t('newRanges')}
        value={stats.newRanges}
        description={t('newRangesDescription')}
        icon={<FiberNewOutlined />}
        variant="success"
        loading={isStatsLoading && !hasLoadedOnce} />,
    ],
  };

  return (
    <>
      {children({ filterBar, headlineContent, table, isInitialLoading: isLoading && !hasLoadedOnce })}
      <ConfirmationDialog
        open={Boolean(deleteCandidate)}
        icon={<Trash01 />}
        variant="bad"
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage')}
        confirmButtonText={isDeleting ? t('deleting') : t('delete')}
        cancelButtonText={t('cancel')}
        isProcessing={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteCandidate(null)}
      />
      {feedback && (
        <MessagesSnackbar
          open={Boolean(feedback)}
          onClose={() => setFeedback(null)}
          message={feedback.message}
          severity={feedback.severity}
          duration={4000}
        />
      )}
    </>
  );
};

export default RangesListPageContent;
